import {UploadOutlined} from '@ant-design/icons'
import {Button, Card, Checkbox, Form, Input, Select, Spin, Tag, Upload} from 'antd'
import {CreativeBean, ItemResponseCreativeBeanObject} from 'common/api/api.ts'
import {creativeApi} from 'common/api/entitiesApi.tsx'
import {AnchorsForm} from 'common/components/AnchorsForm.tsx'
import {ButtonsPanel} from 'common/components/ButtonsPanel.tsx'
import {CreativeStatusSelector} from 'common/components/dictionary/CreativeStatusSelector.tsx'
import {MraidVersionSelector} from 'common/components/dictionary/MraidVersionSelector'
import {FormCard} from 'common/components/FormCard.tsx'
import {AdvertiserSelector} from 'common/components/reloadable/AdvertiserSelector'
import {StatusSwitch} from 'common/components/StatusSwitch.tsx'
import {useAppContext} from 'common/context/AppContext.tsx'
import {CommonRoutes} from 'common/context/commonRoutes.ts'
import {env} from 'common/env'
import PictureUpload, {PictureUploadRef} from 'common/pages/creative/PictureUpload.tsx'
import {queueListRepositionOnModifiedChange} from 'common/util/listNavigation.ts';
import {apiCall, formatNames, FormMode, getCopyName, refusedReasonNames, showError} from 'common/util/Util.tsx'
import React, {useEffect, useRef, useState} from 'react'
import {useNavigate, useParams} from 'react-router-dom'
import SizeSelector, {SizeFormatType} from '../../components/SizeSelector.tsx'

import CharacterCountdownInput from './CharacterCountdownInput.tsx'
import creativeFetchCall from './CreativeFetchCall.ts'
import {
    contextOptions,
    nativeIconHeight,
    nativeIconWidth,
    nativeImageHeight,
    nativeImageWidth,
    placementOptions,
    pushIconHeight,
    pushIconWidth,
    pushImageHeight,
    pushImageWidth,
    statusColors
} from './CreativeHelper.ts'
import {MacroGuide} from './MacroGuide.tsx'
import {PreviewPanel} from './PreviewPanel.tsx'
import {formatLandingPageUrlInput, normalizeLandingPageUrl, useUtm} from './useUtm.ts'
import {getVideoAspectRatioLabel, videoAspectRatioEnumValues} from './videoAspectRatios.ts'

const FormItem = Form.Item
const Option = Select.Option
const {TextArea} = Input
const CheckboxGroup = Checkbox.Group

type NativeDataPartial = Partial<NonNullable<CreativeBean['nativeData']>>

interface EntityType extends Partial<Omit<CreativeBean, 'nativeData'>> {
    active?: boolean
    nativeData?: NativeDataPartial
}

const initialEntity: EntityType = {
    format: CreativeBean.FormatEnum.ImageLink,
    active: true,
    position: [],
    videoPlacementTypes: [],
    secure: true
}

const SIMPLE_NATIVE_ICON_SIZE = {width: nativeIconWidth, height: nativeIconHeight}
const SIMPLE_NATIVE_IMAGE_SIZE = {width: nativeImageWidth, height: nativeImageHeight}

const CreativeForm: React.FC<{ mode: FormMode; agency?: boolean }> = ({mode, agency}) => {
    const {id} = useParams<{ id?: string }>()
    const idNum = id ? parseInt(id) : 0
    const navigateTo = useNavigate()
    const {
        parentAdvertiserId,
        setParentAdvertiserId,
        setBackToAdGroupId,
        setBackToAdGroupMode,
        backToAdGroupId,
        backToAdGroupMode,
        setCreativeIdToLink
    } = useAppContext()
    const appContext = useAppContext()
    const isEdit = mode === FormMode.edit
    const isCopy = mode === FormMode.copy
    const isEditOrCopy = isEdit || isCopy
    const [form] = Form.useForm()
    const [entity, setEntity] = useState<EntityType>(initialEntity)

    const [mainMediaFile, setMainMediaFile] = useState<File | undefined>()
    const [pushIconFile, setPushIconFile] = useState<File | undefined>()
    const [simpleNativeMainFile, setSimpleNativeMainFile] = useState<File | undefined>()
    const [simpleNativeIconFile, setSimpleNativeIconFile] = useState<File | undefined>()
    const [nativeImagesFiles, setNativeImagesFiles] = useState<File[]>([])
    const [nativeVideosFiles, setNativeVideosFiles] = useState<File[]>([])
    const simpleNativeImageUploaderRef = useRef<PictureUploadRef>(null)
    const simpleNativeIconUploaderRef = useRef<PictureUploadRef>(null)
    const mainMediaUploaderRef = useRef<PictureUploadRef>(null)
    const landingPageAllSelectedRef = useRef(false)

    const [loading, setLoading] = useState(false)
    const [previewStatusOk, setPreviewStatusOk] = useState(false)
    const [bannerImageError, setBannerImageError] = useState<string | undefined>()
    const [pushIconError, setPushIconError] = useState<string | undefined>()
    const [simpleNativeImageError, setSimpleNativeImageError] = useState<string | undefined>()

    const format = entity.format as CreativeBean.FormatEnum
    const {url, withTags, toggleTags, changeRaw, applyFormat} = useUtm('')
    const isPush = format === CreativeBean.FormatEnum.Push
    const isNative = format === CreativeBean.FormatEnum.NativeAds
    const isSimpleNative = format === CreativeBean.FormatEnum.SimpleNativeAds
    const isImageLink = format === CreativeBean.FormatEnum.ImageLink
    const isHtml = format === CreativeBean.FormatEnum.HTML
    const isHtml5 = format === CreativeBean.FormatEnum.HTML5
    const isVideo = format === CreativeBean.FormatEnum.Video
    const isVideoVast =
        format === CreativeBean.FormatEnum.VideoVast || format === (CreativeBean.FormatEnum as any).VideoVAST
    const isAudio = format === CreativeBean.FormatEnum.Audio

    function creativeFormatToSizeFormat(format?: CreativeBean.FormatEnum) {
        if (format)
            switch (format) {
                case CreativeBean.FormatEnum.Video:
                    return SizeFormatType.VIDEO
                case CreativeBean.FormatEnum.VideoVast:
                    return SizeFormatType.VIDEO
                case CreativeBean.FormatEnum.ImageLink:
                    return SizeFormatType.BANNER
                case CreativeBean.FormatEnum.HTML:
                case CreativeBean.FormatEnum.HTML5:
                    return SizeFormatType.BANNER
                default:
                    return undefined
            }
        else
            return undefined
    }

    useEffect(() => {
        if (!isEditOrCopy) {
            form.setFieldsValue(initialEntity)
            return
        }
        if (!id) return
        let mounted = true
        setLoading(true)
        creativeApi
            .getCreative(idNum)
            .then((resp: ItemResponseCreativeBeanObject) => {
                if (!mounted) return
                const bean = resp.data as CreativeBean
                const base: EntityType = {
                    ...bean,
                    nativeData: bean.nativeData ? {...bean.nativeData} : undefined
                }
                if (isCopy) {
                    delete base.id
                    base.status = undefined
                    base.refusedReason = undefined
                    base.name = getCopyName(bean.name)
                    if (bean.format === CreativeBean.FormatEnum.HTML5) base.media = undefined
                }

                setEntity(base)
                form.setFieldsValue(base)
                changeRaw(bean.finalUrl || '')
                if (bean.format === CreativeBean.FormatEnum.HTML || bean.format === CreativeBean.FormatEnum.HTML5) {
                    fetch(`${env.creativePreview}?id=${id}`)
                        .then((r) => mounted && setPreviewStatusOk(r.status === 200))
                        .catch(() => mounted && setPreviewStatusOk(false))
                }
                setLoading(false)
            })
            .catch((e) => {
                if (!mounted) return
                setLoading(false)
                showError(appContext, e)
            })
        return () => {
            mounted = false
        }
    }, [isEditOrCopy, id, idNum, isCopy])

    useEffect(() => {
        form.setFieldValue('finalUrl', url)
    }, [url, form])

    const updateNativeData = (patch: NativeDataPartial | ((prev: NativeDataPartial) => NativeDataPartial)) => {
        setEntity((prev) => {
            const current = prev.nativeData || {}
            const next = typeof patch === 'function' ? patch(current) : patch
            return {...prev, nativeData: {...current, ...next}}
        })
    }

    const handleFormatChange = (value: CreativeBean.FormatEnum) => {
        form.resetFields(['adCode', 'nativeData', 'aspectRatio'])

        setMainMediaFile(undefined)
        mainMediaUploaderRef.current?.clearFiles()
        setPushIconFile(undefined)
        setSimpleNativeMainFile(undefined)
        setSimpleNativeIconFile(undefined)
        setNativeImagesFiles([])
        setNativeVideosFiles([])
        setBannerImageError(undefined)
        setPushIconError(undefined)
        setSimpleNativeImageError(undefined)

        setEntity((prev) => ({
            ...prev,
            format: value,
            media: undefined,
            aspectRatio: value === CreativeBean.FormatEnum.Video ? undefined : prev.aspectRatio,
            width: value === CreativeBean.FormatEnum.Video ? undefined : prev.width,
            height: value === CreativeBean.FormatEnum.Video ? undefined : prev.height,
            nativeData: value === CreativeBean.FormatEnum.SimpleNativeAds ? {} : prev.nativeData
        }))
        applyFormat(value)
    }

    const onContextChange = (list: any[]) => {
        updateNativeData((nd) => ({...nd, contextTypeList: list}))
    }
    const onPlacementChange = (list: any[]) => {
        updateNativeData((nd) => ({...nd, placementTypeList: list}))
    }

    const validateBeforeSubmit = () => {
        if (isHtml5 && (entity.width == undefined || entity.height == undefined)) {
            setBannerImageError('Select the HTML5 creative size first')
            document.querySelector('[data-block="media-block"]')?.scrollIntoView({behavior: 'smooth', block: 'center'})
            return false
        }
        if (isVideo && !entity.aspectRatio) {
            setBannerImageError('Video aspect ratio is required')
            document.querySelector('[data-block="aspect-ratio-block"]')?.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
            })
            return false
        }
        if (isImageLink || isPush || isAudio || isVideo || isHtml5) {
            const hasExisting = !!entity.media && (!isHtml5 || isEdit)
            if (!mainMediaFile && !hasExisting) {
                setBannerImageError(
                    isAudio ? 'Audio file is required' : isVideo ? 'Video file is required' : isHtml5 ? 'HTML5 ZIP package is required' : 'Image must be set'
                )
                document.querySelector('[data-block="media-block"]')?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                })
                return false
            }
        }
        if (isPush) {
            const hasExistingIcon = !!entity.icon
            if (!pushIconFile && !hasExistingIcon) {
                setPushIconError('Small Icon must be set')
                document.querySelector('[data-block="push-icon"]')?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                })
                return false
            }
        }
        if (isSimpleNative) {
            const existingList = (entity.nativeData || {}).nativeImageList || {}
            const hasExistingMain = !!(existingList as any)['0']
            if (!simpleNativeMainFile && !hasExistingMain) {
                setSimpleNativeImageError('Image must be set')
                document
                    .querySelector('[data-block="simple-native-image"]')
                    ?.scrollIntoView({behavior: 'smooth', block: 'center'})
                return false
            }
        }
        return true
    }

    const buildSimpleNativeAdCode = (values: any) => {
        const existingList = (values.nativeData || {}).nativeImageList || {}
        const assets: any[] = [{title: {text: '{TITLE}'}}, {data: {type: 2, value: '{DESCRIPTION}'}}]
        if (values.brandName) assets.push({data: {type: 1, value: values.brandName}})
        if (values.cta) assets.push({data: {type: 12, value: values.cta}})
        if (simpleNativeMainFile || (existingList as any)['0']) {
            assets.push({
                img: {type: 3, url: '{IMAGE-0}', w: SIMPLE_NATIVE_IMAGE_SIZE.width, h: SIMPLE_NATIVE_IMAGE_SIZE.height}
            })
        }
        if (simpleNativeIconFile || (existingList as any)['1']) {
            assets.push({
                img: {type: 1, url: '{IMAGE-1}', w: SIMPLE_NATIVE_ICON_SIZE.width, h: SIMPLE_NATIVE_ICON_SIZE.height}
            })
        }
        values.nativeData = {
            ...(values.nativeData || {}),
            adCode: JSON.stringify({link: {url: '{CLICK_URL}'}, assets})
        }
        values.iconWidth = SIMPLE_NATIVE_ICON_SIZE.width
        values.iconHeight = SIMPLE_NATIVE_ICON_SIZE.height
        values.width = SIMPLE_NATIVE_IMAGE_SIZE.width
        values.height = SIMPLE_NATIVE_IMAGE_SIZE.height
    }

    const leaveForm = (resp?: any) => {
        if (backToAdGroupId !== undefined) {
            setCreativeIdToLink(resp?.data?.id)
            navigateTo(
                backToAdGroupMode === FormMode.create ? CommonRoutes.AD_GROUP_CREATE : `/ad-group/${backToAdGroupId}/${backToAdGroupMode}`
            )
        } else {
            navigateTo(CommonRoutes.CREATIVE)
        }
    }

    const handleSubmit = () => {
        form.validateFields().then((formValues) => {
            if (!validateBeforeSubmit()) return
            setLoading(true)
            const values: any = {...entity, ...formValues}
            values.finalUrl = normalizeLandingPageUrl(values.finalUrl || '')

            if (isPush) {
                values.iconWidth = pushIconWidth
                values.iconHeight = pushIconHeight
                values.width = pushImageWidth
                values.height = pushImageHeight
            }
            if (isSimpleNative) {
                values.nativeData = {...(entity.nativeData || {}), ...(values.nativeData || {})}
                if (!values.nativeData.nativeImageList && entity.nativeData?.nativeImageList) {
                    values.nativeData.nativeImageList = {...(entity.nativeData.nativeImageList as any)}
                }
                if (!values.nativeData.nativeVideoList && entity.nativeData?.nativeVideoList) {
                    values.nativeData.nativeVideoList = {...(entity.nativeData.nativeVideoList as any)}
                }
                buildSimpleNativeAdCode(values)
            } else if (isNative) {
                values.nativeData = {...(entity.nativeData || {}), ...(values.nativeData || {})}
            }
            if (!values.width || !values.height) {
                values.width = 1
                values.height = 1
            }
            if ((isImageLink || isPush || isAudio || isVideo || (isHtml5 && isEdit)) && !mainMediaFile && entity.media) {
                values.media = entity.media
            }
            if (isPush && !pushIconFile && entity.icon) {
                values.icon = entity.icon
            }
            const iconFileToSend = isPush ? pushIconFile : isSimpleNative ? simpleNativeIconFile : undefined
            let nativeImagesArg: File[] = []
            if (isSimpleNative) {
                const arr: File[] = []
                if (simpleNativeMainFile) arr.push(simpleNativeMainFile)
                nativeImagesArg = arr
            } else if (isNative) {
                nativeImagesArg = nativeImagesFiles
            }
            const request =
                isEdit && idNum
                    ? creativeFetchCall.edit(
                        idNum,
                        {id: idNum, ...values},
                        iconFileToSend,
                        mainMediaFile,
                        nativeImagesArg,
                        nativeVideosFiles
                    )
                    : creativeFetchCall.create({...values}, iconFileToSend, mainMediaFile, nativeImagesArg, nativeVideosFiles)
            request.then((response) => {
                queueListRepositionOnModifiedChange({
                    appContext,
                    routeKey: CommonRoutes.CREATIVE,
                    previousEntity: entity,
                    responseEntity: response?.data,
                    enabled: isEdit && backToAdGroupId === undefined
                })

                leaveForm(response)
            }).catch((e) => {
                setLoading(false)
                showError(appContext, e)
            })
        })
    }

    const handleLandingPageInput = (event: React.ChangeEvent<HTMLInputElement>) => {
        const previousValue = form.getFieldValue('finalUrl') || ''
        const isNewInput = !previousValue || landingPageAllSelectedRef.current
        const nextValue = formatLandingPageUrlInput(event.target.value, isNewInput)
        landingPageAllSelectedRef.current = false
        changeRaw(nextValue)
        return nextValue
    }

    const handleLandingPageBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        landingPageAllSelectedRef.current = false
        const normalized = normalizeLandingPageUrl(event.target.value)
        changeRaw(normalized)
        form.setFieldValue('finalUrl', normalized)
    }

    const handleLandingPageSelect = (event: React.SyntheticEvent<HTMLInputElement>) => {
        const {selectionStart, selectionEnd, value} = event.currentTarget
        landingPageAllSelectedRef.current = value.length > 0 && selectionStart === 0 && selectionEnd === value.length
    }

    const deleteCreative = () => {
        apiCall(setLoading, appContext, () => creativeApi.deleteCreative(idNum), leaveForm)
    }

    useEffect(() => {
        return () => {
            setParentAdvertiserId(undefined)
            setBackToAdGroupId(undefined)
            setBackToAdGroupMode(undefined)
            setCreativeIdToLink(undefined)
        }
    }, [setParentAdvertiserId, setBackToAdGroupId, setBackToAdGroupMode, setCreativeIdToLink])

    const clearMainMedia = () => {
        setMainMediaFile(undefined)
        mainMediaUploaderRef.current?.clearFiles()
        setBannerImageError(undefined)
        setEntity((prev) => ({...prev, media: undefined}))
    }

    const uploadDisabled = () => {
        if (isVideo) return !entity.aspectRatio
        return !isPush && !isAudio && (entity.width == undefined || entity.height == undefined)
    }

    return (
        <Spin spinning={loading}>
            <AnchorsForm form={form} onFinish={handleSubmit}>
                <FormCard
                    id="general"
                    title={
                        <>
                            Creative
                            {entity.status && (
                                <Tag
                                    color={statusColors[entity.status]}
                                    style={{marginLeft: '14px', height: '24px', color: '#343D48'}}
                                >
                                    {entity.status}
                                </Tag>
                            )}
                        </>
                    }
                    extra={<StatusSwitch entity={entity} setEntity={setEntity}/>}
                    styles={{header: {borderBottom: 'none'}}}
                >
                    {agency && (
                        <FormItem
                            name="status"
                            rules={[{required: true, message: 'Please select Creative status'}]}
                            label="Status"
                            tooltip="Indicate the current approval state of the creative (e.g., Approved, Pending, Rejected)."
                            initialValue={CreativeBean.StatusEnum.Approved}
                        >
                            <CreativeStatusSelector onChange={v => setEntity({...entity, status: v})}/>
                        </FormItem>
                    )}
                    {entity.status === CreativeBean.StatusEnum.Refused && (
                        <FormItem
                            name="refusedReason"
                            label="Refused Reason"
                            rules={[{required: true, message: 'Please select Refused Reason'}]}
                        >
                            <Select
                                open={agency ? undefined : false}
                                style={!agency ? {cursor: 'default', background: 'none', color: 'inherit'} : undefined}
                            >
                                {Object.values(CreativeBean.RefusedReasonEnum).map((f) => (
                                    <Option key={f} value={f}>
                                        {refusedReasonNames[f]}
                                    </Option>
                                ))}
                            </Select>
                        </FormItem>
                    )}
                    {agency && (
                        <FormItem
                            required
                            label="Advertiser"
                            tooltip="Select the advertiser that owns this creative."
                            name="advertiserId"
                            rules={[{required: true, message: 'Advertiser is required'}]}
                            initialValue={parentAdvertiserId}
                        >
                            <AdvertiserSelector disabled={mode === 'edit' || !!parentAdvertiserId}/>
                        </FormItem>
                    )}
                    <FormItem name="name" label="Name"
                              rules={[{required: true, message: 'Please input creative name'}]}>
                        <Input onChange={(e) => setEntity((prev) => ({...prev, name: e.target.value}))}/>
                    </FormItem>
                    <FormItem
                        name="format"
                        label="Format"
                        tooltip="Specifies the type of creative (e.g., Banner, Video, Native)."
                        rules={[{required: true, message: 'Please select format'}]}
                        initialValue={CreativeBean.FormatEnum.ImageLink}
                    >
                        <Select placeholder="Select type"
                                onChange={(v: CreativeBean.FormatEnum) => handleFormatChange(v)}>
                            {(Object.values(CreativeBean.FormatEnum) as CreativeBean.FormatEnum[])
                                .filter((f) => f !== CreativeBean.FormatEnum.NativeAds || f === format)
                                .map((f) => (
                                    <Option key={f} value={f}>
                                        {formatNames[f]}
                                    </Option>
                                ))}
                        </Select>
                    </FormItem>
                    <FormItem
                        name="position"
                        label="Placement Position"
                        tooltip="Choose the desired position on the page where the creative should appear (if applicable)."
                        style={{
                            display: [
                                CreativeBean.FormatEnum.ImageLink,
                                CreativeBean.FormatEnum.HTML,
                                CreativeBean.FormatEnum.HTML5,
                                CreativeBean.FormatEnum.VideoVast
                            ].includes(format)
                                ? ''
                                : 'none'
                        }}
                    >
                        <Select
                            allowClear
                            mode="multiple"
                            placeholder="Select position"
                            onChange={(val) => setEntity((prev) => ({...prev, position: val}))}
                        >
                            {Object.values(CreativeBean.PositionEnum).map((p) => (
                                <Option key={p} value={p}>
                                    {p}
                                </Option>
                            ))}
                        </Select>
                    </FormItem>
                    <FormItem
                        name="finalUrl"
                        label="Landing Page (URL)"
                        tooltip="The destination URL where users will be directed after clicking the ad."
                        rules={[{required: true, message: 'Please input Landing Page URL'}]}
                        style={{marginBottom: '5px'}}
                        getValueFromEvent={handleLandingPageInput}
                    >
                        <Input
                            placeholder="https://"
                            onSelect={handleLandingPageSelect}
                            onBlur={handleLandingPageBlur}
                        />
                    </FormItem>
                    <FormItem label=" " colon={false}>
                        <Checkbox checked={withTags} onChange={(e) => toggleTags(e.target.checked, format)}>
                            Add content type to UTM tag
                        </Checkbox>
                    </FormItem>

                    {isVideo && (
                        <FormItem
                            data-block="aspect-ratio-block"
                            name="aspectRatio"
                            label="Aspect Ratio"
                            rules={[{required: true, message: 'Please select video aspect ratio'}]}
                        >
                            <Select
                                placeholder="Select aspect ratio"
                                onChange={(aspectRatio: CreativeBean.AspectRatioEnum) => {
                                    setEntity((prev) => ({
                                        ...prev,
                                        aspectRatio
                                    }))
                                    clearMainMedia()
                                }}
                            >
                                {videoAspectRatioEnumValues.map((ratio) => (
                                    <Option key={ratio} value={ratio}>
                                        {getVideoAspectRatioLabel(ratio)}
                                    </Option>
                                ))}
                            </Select>
                        </FormItem>
                    )}

                    {(creativeFormatToSizeFormat(entity.format) && !isVideo) &&
                        <FormItem label="Size" required>
                            <SizeSelector
                                format={creativeFormatToSizeFormat(entity.format) || SizeFormatType.BANNER}
                                height={entity.height}
                                width={entity.width}
                                onChange={(width, height) => {
                                    setEntity({...entity, width, height})
                                    clearMainMedia()
                                }}
                            />
                        </FormItem>
                    }

                    {isVideoVast && (
                        <FormItem name="mediaDuration" label="Duration (sec)">
                            <Input
                                type="number"
                                min={1}
                                placeholder="Set video duration in seconds"
                                onChange={(e) =>
                                    setEntity((prev) => ({
                                        ...prev,
                                        mediaDuration: e.target.value ? parseInt(e.target.value, 10) : undefined
                                    }))
                                }
                            />
                        </FormItem>
                    )}

                    {isPush && (
                        <>
                            <CharacterCountdownInput name="title" label="Headline" maxLength={40} required/>
                            <CharacterCountdownInput name="text" label="Description" maxLength={120} required/>
                            <FormItem
                                data-block="push-icon"
                                required
                                style={{height: '250px'}}
                                label={
                                    <span>
                    <br/>
                    Small Icon
                    <br/>
                    Size: {pushIconWidth}x{pushIconHeight} px
                    <br/>
                    Format: PNG, JPG
                  </span>
                                }
                                validateStatus={pushIconError ? 'error' : undefined}
                                help={pushIconError}
                            >
                                <PictureUpload
                                    entries={entity.icon ? ({'0': entity.icon} as any) : undefined}
                                    onComplete={(entries, fl) => {
                                        const hasServer = entries && Object.keys(entries).length > 0
                                        if (fl.length > 0) {
                                            setPushIconFile(fl[0])
                                            setPushIconError(undefined)
                                        } else if (hasServer) {
                                            setPushIconFile(undefined)
                                            setPushIconError(undefined)
                                        } else {
                                            setPushIconFile(undefined)
                                            setEntity((prev) => ({...prev, icon: undefined}))
                                        }
                                    }}
                                    listType="picture-card"
                                    maxCount={1}
                                    dimensions={{width: pushIconWidth, height: pushIconHeight}}
                                    previewSize={{width: '192px', height: '192px'}}
                                />
                            </FormItem>
                        </>
                    )}

                    {(isImageLink || isPush || isAudio || isVideo) && (
                        <FormItem
                            data-block="media-block"
                            label={
                                isPush ? (
                                    <span>
                    <br/>
                    Main Image
                    <br/>
                    Size: {pushImageWidth}x{pushImageHeight} px
                    <br/>
                    Format: PNG, JPG
                  </span>
                                ) : isAudio ? (
                                    'Audio'
                                ) : isVideo ? (
                                    'Video'
                                ) : (
                                    'Image'
                                )
                            }
                            required
                            tooltip={isVideo ? 'Supported formats: MP4, MOV.' : undefined}
                            validateStatus={bannerImageError ? 'error' : undefined}
                            help={bannerImageError}
                            style={{height: isPush ? '250px' : '280px'}}
                        >
                            <>
                                <PictureUpload
                                    ref={mainMediaUploaderRef}
                                    isAudio={isAudio}
                                    isVideo={isVideo}
                                    entries={entity.media ? ({'0': entity.media} as any) : undefined}
                                    onComplete={(entries, fl) => {
                                        const hasServer = entries && Object.keys(entries).length > 0
                                        if (fl.length > 0) {
                                            setMainMediaFile(fl[0])
                                            setBannerImageError(undefined)
                                        } else if (hasServer) {
                                            setMainMediaFile(undefined)
                                            setBannerImageError(undefined)
                                        } else {
                                            setMainMediaFile(undefined)
                                            setEntity((prev) => ({...prev, media: undefined}))
                                        }
                                    }}
                                    listType="picture-card"
                                    maxCount={1}
                                    videoAspectRatio={entity.aspectRatio}
                                    dimensions={
                                        isPush
                                            ? {width: pushImageWidth, height: pushImageHeight}
                                            : (entity.width !== undefined && entity.height !== undefined ?
                                                {width: entity.width, height: entity.height} : undefined)
                                    }
                                    previewSize={isPush ? {width: '360px', height: '240px'} : {
                                        width: '300px',
                                        height: '250px'
                                    }}
                                    disabled={uploadDisabled()}
                                    label={uploadDisabled() ? (isVideo ? 'Select aspect ratio above to enable file upload' : 'Select size (or set custom width/height) above to enable file upload') : 'Upload'}
                                />
                            </>
                        </FormItem>
                    )}

                    {isHtml5 && (
                        <FormItem
                            data-block="media-block"
                            label="HTML5 ZIP"
                            tooltip="ZIP up to 5 MB; index.html or one primary HTML; matching ad.size; clickTag or GWD exits; relative or HTTPS resources only."
                            required
                            validateStatus={bannerImageError ? 'error' : undefined}
                            help={bannerImageError}
                        >
                            <Upload
                                accept=".zip,application/zip"
                                maxCount={1}
                                disabled={uploadDisabled()}
                                fileList={mainMediaFile ? [{
                                    uid: `${mainMediaFile.name}-${mainMediaFile.lastModified}`,
                                    name: mainMediaFile.name,
                                    status: 'done'
                                }] : []}
                                beforeUpload={(file) => {
                                    const validName = file.name.toLowerCase().endsWith('.zip')
                                    const validSize = file.size <= 5 * 1024 * 1024
                                    if (!validName || !validSize) {
                                        setBannerImageError(validName ? 'ZIP package must be 5 MB or smaller' : 'Only ZIP packages are supported')
                                        return Upload.LIST_IGNORE
                                    }
                                    setMainMediaFile(file)
                                    setBannerImageError(undefined)
                                    return false
                                }}
                                onRemove={() => {
                                    setMainMediaFile(undefined)
                                    return true
                                }}
                            >
                                <Button disabled={uploadDisabled()} icon={<UploadOutlined/>}>
                                    {uploadDisabled() ? 'Select size first' : 'Select ZIP package'}
                                </Button>
                            </Upload>
                            {entity.media && !mainMediaFile && (
                                <div style={{marginTop: 8}}>Current package: {entity.media.split('/').pop()}</div>
                            )}
                        </FormItem>
                    )}

                    <FormItem
                        name="videoPlacementTypes"
                        style={{display: isVideoVast ? '' : 'none'}}
                        label="Video Placement Types"
                    >
                        <Select
                            mode="multiple"
                            placeholder="Select Placement Types"
                            onChange={(vals) => setEntity((prev) => ({...prev, videoPlacementTypes: vals}))}
                        >
                            {Object.values(CreativeBean.VideoPlacementTypesEnum).map((t) => (
                                <Option key={t} value={t}>
                                    {t}
                                </Option>
                            ))}
                        </Select>
                    </FormItem>
                    <FormItem
                        name="adCode"
                        style={{display: isHtml || isVideoVast ? '' : 'none'}}
                        label="Ad code"
                        rules={[{required: isHtml || isVideoVast, message: 'Please input ad code'}]}
                    >
                        <TextArea
                            rows={4}
                            style={{marginBottom: 3}}
                            onChange={(e) => setEntity((prev) => ({...prev, adCode: e.target.value}))}
                        />
                    </FormItem>
                    {(isHtml || isHtml5 || isVideoVast) && 
                        <FormItem name="mraid" label="MRAID Version">
                            <MraidVersionSelector placeholder="Not Defined"/>
                        </FormItem>
                    }
                    {(isImageLink || isHtml || isHtml5) && (
                        <>
                            <FormItem
                                name="impressionPixel"
                                label="Impression pixel URL"
                                tooltip="URL of the tracking pixel that fires when the ad is viewed, used to count impressions and verify delivery with third-party services."
                            >
                                <Input
                                    onChange={(e) =>
                                        setEntity((prev) => ({
                                            ...prev,
                                            impressionPixel: e.target.value
                                        }))
                                    }
                                />
                            </FormItem>
                            <FormItem
                                name="thirdPartyTracking"
                                label="3rd Party Tracking"
                                tooltip="Add additional tracking URLs or pixels from external services to monitor impressions."
                            >
                                <TextArea
                                    rows={1}
                                    style={{marginBottom: 3}}
                                    onChange={(e) =>
                                        setEntity((prev) => ({
                                            ...prev,
                                            thirdPartyTracking: e.target.value
                                        }))
                                    }
                                />
                            </FormItem>
                        </>
                    )}
                    {(isNative || isSimpleNative) && (
                        <>
                            <CharacterCountdownInput name={['nativeData', 'title']} label="Headline" maxLength={50}
                                                     required/>
                            <CharacterCountdownInput
                                name={['nativeData', 'description']}
                                label="Description"
                                maxLength={140}
                                required
                            />
                        </>
                    )}
                    {isSimpleNative && (
                        <>
                            <CharacterCountdownInput name="brandName" label="Brand Name" maxLength={30}/>
                            <CharacterCountdownInput name="cta" label="CTA" maxLength={30}/>
                            <FormItem
                                label={
                                    <span>
                    Icon
                    <br/>
                    Size: {SIMPLE_NATIVE_ICON_SIZE.width}x{SIMPLE_NATIVE_ICON_SIZE.height} px
                    <br/>
                    Format: PNG, JPG
                  </span>
                                }
                                style={{height: '155px'}}
                            >
                                <PictureUpload
                                    key={isEditOrCopy ? 'simpleNativeIconEditUnified' : 'simpleNativeIconCreateUnified'}
                                    entries={
                                        (entity.nativeData as any)?.nativeImageList?.['1']
                                            ? {'0': (entity.nativeData as any).nativeImageList['1']}
                                            : undefined
                                    }
                                    maxCount={1}
                                    onComplete={(entries, files) => {
                                        const hasServer = entries && Object.keys(entries).length > 0
                                        if (files[0]) {
                                            setSimpleNativeIconFile(files[0])
                                        } else if (!hasServer) {
                                            setSimpleNativeIconFile(undefined)
                                            updateNativeData((nd) => {
                                                const list = {...(nd.nativeImageList || {})}
                                                delete (list as any)['1']
                                                return {
                                                    ...nd,
                                                    nativeImageList: Object.keys(list).length ? list : undefined
                                                }
                                            })
                                        } else {
                                            setSimpleNativeIconFile(undefined)
                                        }
                                    }}
                                    dimensions={SIMPLE_NATIVE_ICON_SIZE}
                                    previewSize={{width: SIMPLE_NATIVE_ICON_SIZE.width + 'px', height: '125px'}}
                                    ref={simpleNativeIconUploaderRef}
                                />
                            </FormItem>
                            <FormItem
                                data-block="simple-native-image"
                                label={
                                    <span>
                    <br/>
                    Image
                    <br/>
                    Size: {SIMPLE_NATIVE_IMAGE_SIZE.width}x{SIMPLE_NATIVE_IMAGE_SIZE.height} px
                    <br/>
                    Format: PNG, JPG
                  </span>
                                }
                                required
                                style={{height: '160px'}}
                                validateStatus={simpleNativeImageError ? 'error' : undefined}
                                help={simpleNativeImageError}
                            >
                                <PictureUpload
                                    key={isEditOrCopy ? 'simpleNativeImageEditUnified' : 'simpleNativeImageCreateUnified'}
                                    entries={
                                        (entity.nativeData as any)?.nativeImageList?.['0']
                                            ? {'0': (entity.nativeData as any).nativeImageList['0']}
                                            : undefined
                                    }
                                    maxCount={1}
                                    onComplete={(entries, files) => {
                                        const hasServer = entries && Object.keys(entries).length > 0
                                        if (files[0]) {
                                            setSimpleNativeMainFile(files[0])
                                            setSimpleNativeImageError(undefined)
                                        } else if (!hasServer) {
                                            setSimpleNativeMainFile(undefined)
                                            updateNativeData((nd) => {
                                                const list = {...(nd.nativeImageList || {})}
                                                delete (list as any)['0']
                                                return {
                                                    ...nd,
                                                    nativeImageList: Object.keys(list).length ? list : undefined
                                                }
                                            })
                                        } else {
                                            setSimpleNativeMainFile(undefined)
                                        }
                                    }}
                                    dimensions={SIMPLE_NATIVE_IMAGE_SIZE}
                                    previewSize={{width: SIMPLE_NATIVE_IMAGE_SIZE.width / 2 + 'px', height: '160px'}}
                                    ref={simpleNativeImageUploaderRef}
                                />
                            </FormItem>
                        </>
                    )}
                    {isNative && (
                        <>
                            <FormItem label="Context" required>
                                <CheckboxGroup
                                    options={contextOptions as any}
                                    value={(entity.nativeData || {}).contextTypeList || []}
                                    onChange={onContextChange}
                                />
                            </FormItem>
                            <FormItem label="Placement type" required>
                                <CheckboxGroup
                                    options={placementOptions as any}
                                    value={(entity.nativeData || {}).placementTypeList || []}
                                    onChange={onPlacementChange}
                                />
                            </FormItem>
                            <FormItem label="Images">
                                <PictureUpload
                                    entries={(entity.nativeData as any)?.nativeImageList as never}
                                    onComplete={(entries, fl) => {
                                        updateNativeData((nd) => ({...nd, nativeImageList: entries}))
                                        setNativeImagesFiles(fl.filter(Boolean))
                                    }}
                                />
                            </FormItem>
                            <FormItem label="Videos">
                                <PictureUpload
                                    isVideo
                                    entries={(entity.nativeData as any)?.nativeVideoList as never}
                                    onComplete={(entries, fl) => {
                                        updateNativeData((nd) => ({...nd, nativeVideoList: entries}))
                                        setNativeVideosFiles(fl.filter(Boolean))
                                    }}
                                />
                            </FormItem>
                            <FormItem name={['nativeData', 'vastCode']} label="VAST Code">
                                <TextArea
                                    rows={4}
                                    onChange={(e) =>
                                        updateNativeData((nd) => ({
                                            ...nd,
                                            vastCode: e.target.value
                                        }))
                                    }
                                />
                            </FormItem>
                            <FormItem
                                name={['nativeData', 'adCode']}
                                label="Ad Code"
                                rules={[{required: true, message: 'Please input Ad Code'}]}
                            >
                                <TextArea
                                    rows={20}
                                    onChange={(e) =>
                                        updateNativeData((nd) => ({
                                            ...nd,
                                            adCode: e.target.value
                                        }))
                                    }
                                />
                            </FormItem>
                        </>
                    )}
                    {isEditOrCopy && previewStatusOk && (isHtml || isHtml5) && (
                        <FormItem label="Preview">
                            <PreviewPanel
                                id={id}
                                format={format}
                                width={entity.width}
                                height={entity.height}
                                adCode={entity.adCode}
                            />
                        </FormItem>
                    )}
                </FormCard>
                <Card style={{marginTop: '12px'}}>
                    <MacroGuide/>
                </Card>
                <ButtonsPanel onCancel={leaveForm} showDelete={mode === 'edit'} onDelete={deleteCreative}/>
            </AnchorsForm>
        </Spin>
)
}

export default CreativeForm
